import React, { useState, useEffect } from 'react';
import { X, Search, Loader2, Clock } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useDevMode } from '../contexts/DevModeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskModal = ({ isOpen, onClose, task = null, onSuccess, preSelectedProject = null }) => {
  const { token, user } = useAuth();
  const { devMode, dummyData, incrementApiCallCount } = useDevMode();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [hoursRecords, setHoursRecords] = useState([]);
  const [loadingHours, setLoadingHours] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    project: null,
    assignedUsers: [], // Changed to array for multiple users
    title: '',
    description: '',
    hoursExpected: '',
    deadline: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      fetchUsers();
      if (task) {
        fetchTaskHours(task.id);
        // Edit mode - populate form with serializable user data only
        const projectData = task.field_48_raw?.[0];
        const assignedUsersRaw = task.field_61_raw || [];
        const assignedUsersData = assignedUsersRaw.map(u => ({
          id: u.id,
          name: u.field_12?.full || u.field_12_raw?.full || u.field_12_raw || u.identifier || 'Unknown',
          email: u.field_10_raw?.email || u.field_10_raw || ''
        }));
        
        setFormData({
          project: projectData || null,
          assignedUsers: assignedUsersData,
          title: task.field_43_raw || '',
          description: task.field_44_raw || '',
          hoursExpected: task.field_45_raw || '',
          deadline: task.field_46_raw?.date ? convertToHtmlDate(task.field_46_raw.date) : ''
        });
        if (projectData) {
          setProjectSearch(projectData.field_2_raw || projectData.identifier || '');
        }
      } else {
        // Add mode - reset form with default user as serializable data
        console.log('User object from auth:', user);
        const defaultUserName = user?.name || user?.identifier || user?.email?.split('@')[0] || 'Current User';
        console.log('Default user being set:', { id: user.id, name: defaultUserName, email: user.email });
        
        // If preSelectedProject is provided, use it
        const projectData = preSelectedProject ? {
          id: preSelectedProject.id,
          identifier: preSelectedProject.field_2_raw || preSelectedProject.identifier,
          field_2_raw: preSelectedProject.field_2_raw
        } : null;
        
        setFormData({
          project: projectData,
          assignedUsers: [{ id: user.id, name: defaultUserName, email: user.email || '' }],
          title: '',
          description: '',
          hoursExpected: '',
          deadline: ''
        });
        setProjectSearch(projectData ? (projectData.field_2_raw || projectData.identifier || '') : '');
      }
    }
  }, [isOpen, task, user]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      
      // Use dummy data if dev mode is enabled
      if (devMode) {
        console.log('Dev Mode: Using dummy projects');
        setProjects(dummyData.projects);
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/projects`, {
        headers,
        params: { rows_per_page: 1000 }
      });
      incrementApiCallCount();
      console.log('Projects loaded:', response.data.records?.length);
      setProjects(response.data.records || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Use dummy data if dev mode is enabled
      if (devMode) {
        console.log('Dev Mode: Using dummy users');
        setUsers(dummyData.users);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      console.log('Fetching users with token:', token ? 'Token exists' : 'No token');
      
      const response = await axios.get(`${API}/users`, {
        headers,
        params: { rows_per_page: 1000 }
      });
      
      incrementApiCallCount();
      console.log('Full users response:', response.data);
      console.log('Users array:', response.data.records);
      console.log('Users loaded:', response.data.records?.length);
      console.log('First user structure:', response.data.records?.[0]);
      
      // Log all field keys of first user to understand structure
      if (response.data.records?.[0]) {
        const firstUser = response.data.records[0];
        console.log('First user field keys:', Object.keys(firstUser));
        console.log('=== ALL FIELD VALUES ===');
        console.log('Field 17:', firstUser.field_17, '| Raw:', firstUser.field_17_raw);
        console.log('Field 18:', firstUser.field_18, '| Raw:', firstUser.field_18_raw);
        console.log('Field 19:', firstUser.field_19, '| Raw:', firstUser.field_19_raw);
        console.log('Field 20:', firstUser.field_20, '| Raw:', firstUser.field_20_raw);
        console.log('Field 21:', firstUser.field_21, '| Raw:', firstUser.field_21_raw);
        console.log('Profile keys:', firstUser.profile_keys);
        console.log('======================');
      }
      
      const usersList = response.data.records || [];
      console.log('Setting users state with:', usersList);
      setUsers(usersList);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
    }
  };

  const fetchTaskHours = async (taskId) => {
    try {
      setLoadingHours(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/hours`, {
        headers,
        params: { rows_per_page: 100 }
      });
      
      // Filter hours for this specific task
      const taskHours = (response.data.records || []).filter(hour => {
        const taskConnections = hour.field_111_raw || [];
        return taskConnections.some(t => t.id === taskId);
      });
      
      console.log('Hours records for task:', taskHours.length);
      setHoursRecords(taskHours);
    } catch (error) {
      console.error('Failed to fetch task hours:', error);
    } finally {
      setLoadingHours(false);
    }
  };

  const convertToHtmlDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return '';
  };

  const convertToKnackDate = (htmlDate) => {
    if (!htmlDate) return '';
    const [year, month, day] = htmlDate.split('-');
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const filteredProjects = projects.filter(project => {
    const projectName = project.field_5_raw?.toLowerCase() || '';
    const projectCode = project.identifier?.toLowerCase() || '';
    const projectGE = project.field_2_raw?.toLowerCase() || '';
    const search = projectSearch.toLowerCase();
    return projectName.includes(search) || projectCode.includes(search) || projectGE.includes(search);
  });

  const handleProjectSelect = (project) => {
    setFormData({ ...formData, project });
    setProjectSearch(project.field_2_raw || project.identifier || project.field_5_raw || '');
    setShowProjectDropdown(false);
  };

  const handleUserToggle = (selectedUser) => {
    // Only store essential serializable data, not the full user object
    // Use field_12 for user name (standard Knack name field)
    const userName = selectedUser.field_12?.full || 
                     selectedUser.field_12_raw?.full || 
                     selectedUser.field_12_raw || 
                     selectedUser.identifier || 
                     'Unknown User';
    
    const userData = {
      id: selectedUser.id,
      name: userName,
      email: selectedUser.field_10_raw?.email || selectedUser.field_10_raw || ''
    };
    
    const isSelected = formData.assignedUsers.some(u => u.id === selectedUser.id);
    if (isSelected) {
      // Remove user
      setFormData({
        ...formData,
        assignedUsers: formData.assignedUsers.filter(u => u.id !== selectedUser.id)
      });
    } else {
      // Add user with only serializable data
      setFormData({
        ...formData,
        assignedUsers: [...formData.assignedUsers, userData]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.project || formData.assignedUsers.length === 0) {
      alert('Please fill in the task title, select a project, and assign at least one user');
      return;
    }

    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Build connection objects with id and identifier
      const projectConnection = {
        id: formData.project.id,
        identifier: formData.project.field_2_raw || formData.project.identifier || ''
      };
      
      const userConnections = formData.assignedUsers.map(u => ({
        id: u.id,
        identifier: u.name || u.identifier || ''
      }));
      
      const payload = {
        field_43: formData.title,
        field_43_raw: formData.title,
        field_44: formData.description,
        field_44_raw: formData.description,
        field_45: formData.hoursExpected ? parseFloat(formData.hoursExpected) : 0,
        field_45_raw: formData.hoursExpected ? parseFloat(formData.hoursExpected) : 0,
        field_46: formData.deadline ? convertToKnackDate(formData.deadline) : '',
        field_46_raw: formData.deadline ? { date: convertToKnackDate(formData.deadline) } : null,
        field_48: [projectConnection],
        field_48_raw: [projectConnection],
        field_61: userConnections,
        field_61_raw: userConnections,
        field_50: false, // Not completed
        field_50_raw: false,
        // Copy project's country code to task for flag display
        field_117: formData.project.field_117_raw || formData.project.field_117 || '',
        field_117_raw: formData.project.field_117_raw || formData.project.field_117 || ''
      };

      if (task) {
        // Update existing task
        await axios.put(`${API}/tasks/${task.id}`, payload, { headers });
      } else {
        // Create new task
        await axios.post(`${API}/tasks`, payload, { headers });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save task:', error);
      alert('Failed to save task. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {task ? 'Edit Task' : 'Add New Task'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Hours Display (Edit Mode Only) */}
          {task && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Hours Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Registered Hours:</span>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{task.field_183_raw || 0} hrs</p>
                </div>
                <div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">Expected Hours:</span>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{task.field_45_raw || 0} hrs</p>
                </div>
              </div>
              {task.field_45_raw > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                    <span>Progress</span>
                    <span>{Math.min(Math.round(((task.field_183_raw || 0) / task.field_45_raw) * 100), 100)}%</span>
                  </div>
                  <div className="bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-blue-600 transition-all"
                      style={{ width: `${Math.min(((task.field_183_raw || 0) / task.field_45_raw) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              )}
              
              {/* Hours Records List */}
              {loadingHours ? (
                <div className="mt-3 text-center">
                  <Loader2 className="w-4 h-4 animate-spin inline text-blue-600" />
                  <span className="text-xs text-gray-600 dark:text-gray-400 ml-2">Loading hours...</span>
                </div>
              ) : hoursRecords.length > 0 ? (
                <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-700">
                  <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Registered Hours:</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {hoursRecords.map((hour, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-700 px-2 py-1 rounded">
                        <span>{hour.field_113 || 'No date'}</span>
                        <span className="font-medium">{hour.field_112_raw || 0} hrs</span>
                        {hour.field_114_raw && (
                          <span className="text-gray-500 dark:text-gray-500 truncate ml-2 flex-1" title={hour.field_114_raw}>
                            {hour.field_114_raw}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Project Dropdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              GE / Project <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => {
                    setProjectSearch(e.target.value);
                    setShowProjectDropdown(true);
                  }}
                  onFocus={() => setShowProjectDropdown(true)}
                  placeholder="Search projects..."
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              {showProjectDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {loading ? (
                    <div className="p-4 text-center">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-600" />
                    </div>
                  ) : filteredProjects.length > 0 ? (
                    filteredProjects.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() => handleProjectSelect(project)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white"
                      >
                        <div className="font-medium">{project.field_2_raw || project.identifier || 'No GE'}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {project.field_5_raw || 'Untitled Project'}
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      No projects found
                    </div>
                  )}
                </div>
              )}
            </div>
            {formData.project && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Selected: <span className="font-medium">{formData.project.field_2_raw || formData.project.identifier || 'GE'}</span> - {formData.project.field_5_raw}
              </div>
            )}
          </div>

          {/* Assigned Users - Multiple Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Assigned To <span className="text-red-500">*</span>
            </label>
            
            {/* Show currently assigned users */}
            {formData.assignedUsers.length > 0 && (
              <div className="mb-2 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Currently Assigned:</p>
                <div className="flex flex-wrap gap-1">
                  {formData.assignedUsers.map((u) => (
                    <span key={u.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-xs rounded">
                      {u.name || u.identifier || u.id}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg max-h-60 overflow-y-auto bg-white dark:bg-gray-700">
              {loading ? (
                <div className="p-4 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-600" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">Loading users...</p>
                </div>
              ) : users.length > 0 ? (
                <div className="divide-y divide-gray-200 dark:divide-gray-600">
                  {users.map((u) => {
                    const userName = u.field_12?.full || u.field_12_raw?.full || u.field_12_raw || u.identifier || 'No Name';
                    const isSelected = formData.assignedUsers.some(selectedUser => selectedUser.id === u.id);
                    
                    return (
                      <label
                        key={u.id}
                        className="flex items-start gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleUserToggle(u)}
                          className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{userName}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">No users loaded from API</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Check browser console for errors</p>
                </div>
              )}
            </div>
            {formData.assignedUsers.length > 0 && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Selected: <span className="font-medium">{formData.assignedUsers.length} user(s)</span>
              </div>
            )}
          </div>

          {/* Task Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Task Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Enter task title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="Enter task description"
            />
          </div>

          {/* Hours Expected */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hours Expected
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={formData.hoursExpected}
              onChange={(e) => setFormData({ ...formData, hoursExpected: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="0"
            />
          </div>

          {/* Deadline */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Deadline
            </label>
            <input
              type="date"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.title || !formData.project || formData.assignedUsers.length === 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center justify-center"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                task ? 'Update Task' : 'Create Task'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskModal;
